import React, { useEffect, useState } from "react";
import { Upload, ArrowLeft, Check, IndianRupee, X } from "lucide-react";
import vendor_profile_icon from "../../../assets/profile_icon.svg";
import { useAlert } from "components/UI_components/Alert";
import { BASE_URL } from "utils/constant";

const AdvancePayment = ({ vendor, onClose }) => {
  const [paymentDate, setPaymentDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [paidAmount, setPaidAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("Cash");
  const [remarks, setRemarks] = useState("");
  const [isToday, setIsToday] = useState(true);
  const [receiptFile, setReceiptFile] = useState(null);
  const organization = JSON.parse(localStorage.getItem("organization"));
  const { addAlert } = useAlert();
  
  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    setIsToday(paymentDate === today);
  }, [paymentDate]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = [
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/gif',
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ];
      
      if (!validTypes.includes(file.type)) {
        addAlert("Please upload an image, PDF, or document file", "error");
        return;
      }
      
      // Validate file size (e.g., 5MB max)
      if (file.size > 5 * 1024 * 1024) {
        addAlert("File size should be less than 5MB", "error");
        return;
      }
      
      setReceiptFile(file);
    }
  };

  const handleRemoveFile = () => {
    setReceiptFile(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const accessToken = localStorage.getItem("access");
      
      // Create FormData object
      const formData = new FormData();
      formData.append('vendor', vendor.id);
      formData.append('amount', paidAmount);
      formData.append('payment_date', paymentDate);
      formData.append('payment_method', paymentMethod);
      formData.append('remarks', remarks);
      
      // Append file if exists
      if (receiptFile) {
        formData.append('receipt', receiptFile);
      }
      
      const response = await fetch(
        `${BASE_URL}/inventory/create-vendor-advance-payment/`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            // Don't set Content-Type header - browser will set it automatically with boundary
          },
          body: formData,
        }
      );

      const data = await response.json();
      if (data.status) {
        console.log("Advance payment recorded successfully:", data);
        addAlert("Advance payment recorded successfully", "success");
        onClose();
      } else {
        console.error("Error recording advance payment:", data.error);
        addAlert("Error recording advance payment: " + data.error, "error");
      }
    } catch (error) {
      console.error("Error recording advance payment:", error);
      addAlert("Error recording advance payment: " + error.message, "error");
    }
  };

  return (
    <div className="w-full mx-auto p-6 h-[90vh] overflow-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button onClick={onClose} className="text-[#232323] hover:text-black">
          <ArrowLeft className="w-5 h-5" />
        </button>

        <p className="text-[#232323] text-[20px] ml-[450px]">Advance Payment</p>
      </div>

      <div className="flex justify-between gap-12 border bg-white rounded-[12px] border-gray-300 p-3">
        {/* Left */}
        <div className="w-1/2 space-y-[30px]">
          <div className="flex items-center gap-3">
            <img src={vendor_profile_icon} alt="Vendor" className="w-10 h-10" />
            <div>
              <div className="text-[#232323] text-[18px]">
                {vendor?.name}
                <span className="text-[12px] bg-gray-200 px-2 py-1 rounded-full ml-2 text-gray-600">
                  Vendor
                </span>
              </div>
            </div>
          </div>

          <p className="text-[#232323] text-[15px]">
            Purchase Code:{" "}
            <span className="text-[#3B0069] text-[14px] bg-gray-200 px-2 py-1 rounded-full ml-2 ">
              #{organization?.organization_name}_Advpay
            </span>
          </p>

          <div className="mt-4">
            <label className="text-[#232323] text-[15px] mb-1 block">
              Select Payment Date
            </label>
            <div className="flex gap-3 items-center">
              <button
                onClick={() => {
                  const today = new Date().toISOString().split("T")[0];
                  setPaymentDate(today);
                }}
                className={` ${
                  isToday
                    ? "border-1 border-[#7F599D] text-[#3B0069] bg-[#ECE6F0]"
                    : "bg-gray-200"
                } h-[36px]   rounded-[12px] px-5 py-1 text-sm font-medium`}
              >
                Now
              </button>
              <input
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                className="border rounded-lg px-4 py-2 text-[16px] text-[#232323] w-full bg-gray-100"
              />
            </div>
          </div>

          <div className="pt-4">
            <p className="text-[#232323] text-[18px] mb-2">Payment</p>
            <hr className="mb-4" />
            <div className="flex items-center gap-4 mb-4">
              <label className="text-[#232323] text-[15px] whitespace-nowrap">
                Paid Amount
              </label>

              <div className="relative w-full">
                <IndianRupee className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 w-4 h-4" />
                <input
                  type="number"
                  placeholder="Amount"
                  className="pl-8 pr-4 py-2 border rounded-lg text-sm w-full bg-gray-100"
                  value={paidAmount}
                  onChange={(e) => setPaidAmount(e.target.value)}
                />
              </div>
            </div>
            <div className="flex gap-2">
              {["Cash", "Online", "Cheque"].map((method) => (
                <button
                  key={method}
                  onClick={() => setPaymentMethod(method)}
                  className={`px-4 py-1 rounded-full text-sm font-medium ${
                    paymentMethod === method
                      ? "bg-[#ECE6F0] text-[#3B0069] border-1 border-[#3B0069]"
                      : "bg-gray-200 text-gray-700"
                  }`}
                >
                  {method}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Right */}
        <div className="w-1/2 space-y-4">
          <div>
            <label className="text-[#232323] text-[15px] mb-1 block">
              Remarks/ Comments (Optional)
            </label>
            <textarea
              type="text"
              placeholder="Type"
              className="w-full border rounded-lg px-4 py-2 text-sm min-h[60px] bg-gray-100"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
            />
          </div>
          <div>
            <label className="text-[#232323] text-[15px] mb-1 block">
              Payment Slip (Optional)
            </label>
            <input
              type="file"
              id="receiptUpload"
              accept="image/*,.pdf,.doc,.docx"
              onChange={handleFileChange}
              className="hidden"
            />
            
            {!receiptFile ? (
              <label
                htmlFor="receiptUpload"
                className="w-full h-[36px] flex items-center justify-center px-4 py-8 bg-[#ECE6F0] rounded-lg text-sm text-[#3B0069] cursor-pointer hover:bg-purple-100"
              >
                <span className="flex items-center gap-2">
                  <Upload size={16} /> Upload Photos/Documents
                </span>
              </label>
            ) : (
              <div className="w-full flex items-center justify-between px-4 py-3 bg-gray-100 rounded-lg border border-gray-300">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <span className="text-sm text-gray-700 truncate">
                    {receiptFile.name}
                  </span>
                  <span className="text-xs text-gray-500 whitespace-nowrap">
                    ({(receiptFile.size / 1024).toFixed(2)} KB)
                  </span>
                </div>
                <button
                  onClick={handleRemoveFile}
                  className="ml-2 text-red-500 hover:text-red-700"
                  type="button"
                >
                  <X size={18} />
                </button>
              </div>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Supported formats: Images (JPG, PNG, GIF), PDF, DOC, DOCX (Max 5MB)
            </p>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="mt-8 flex justify-center">
        <button
          onClick={handleSubmit}
          className="bg-[#3B0069] text-white px-16 py-2 rounded-lg font-medium hover:bg-purple-800 flex items-center gap-2"
        >
          Save
          <div className="w-6 h-6 rounded-full bg-[#5E2D91] flex items-center justify-center">
            <Check className="w-4 h-4 text-white" />
          </div>
        </button>
      </div>
    </div>
  );
};

export default AdvancePayment;